import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useMutation, useLazyQuery } from "@apollo/client";

import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, Input, Card, Checkbox, PageHeader, Button } from "@cofano/cck";
import { Grid2 as Grid } from "@mui/material";

import { FormErrorMessage } from "components/form/FormErrorMessage";
import Helmet from "components/helmet/Helmet";

import { Language } from "imports/Utils";

import { deliveryConditionSchema, DeliveryConditionSchemaInput, DeliveryConditionSchemaOutput } from "./utils/deliveryCondition.schema";

import { GetDeliveryConditionDocument } from "__generated__/view/deliveryconditions/graphql/deliveryCondition.queries.generated";
import { CreateDeliveryConditionDocument, UpdateDeliveryConditionDocument } from "__generated__/view/deliveryconditions/graphql/deliveryCondition.mutations.generated";
import FormField from "components/cck/FormField";

const DeliveryConditionCreateEdit = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const { control, handleSubmit, reset } = useForm<DeliveryConditionSchemaInput, unknown, DeliveryConditionSchemaOutput>({
		resolver: zodResolver(deliveryConditionSchema),
		defaultValues: {
			id: null,
			key: "",
			description: "",
			showOnLocationPlanning: false,
			showOnBulkPlanning: false,
		},
	});

	const [ getDeliveryCondition ] = useLazyQuery(GetDeliveryConditionDocument, {
		onCompleted: response => reset(response.deliveryCondition),
	});

	const [ createDeliveryCondition, { loading: createLoading } ] = useMutation(CreateDeliveryConditionDocument);
	const [ updateDeliveryCondition, { loading: updateLoading } ] = useMutation(UpdateDeliveryConditionDocument);

	const loading = createLoading || updateLoading;

	const save = (data: DeliveryConditionSchemaOutput) => {

		const variables = {
			input: {
				id: data.id,
				description: data.description,
				showOnLocationPlanning: data.showOnLocationPlanning,
				showOnBulkPlanning: data.showOnBulkPlanning,
			},
			key: data.key,
		};

		if (data.id) {
			updateDeliveryCondition({
				variables: variables,
			}).then(() => {
				navigate("/view/deliveryconditions");
			});
		} else {
			createDeliveryCondition({
				variables: variables,
			}).then(() => {
				navigate("/view/deliveryconditions");
			});
		}
	};

	useEffect(() => {
		if (id) {
			getDeliveryCondition({
				variables: {
					id: Number(id),
				},
			});
		}
	}, [ id ]);

	return (
		<form onSubmit={handleSubmit(save)}>
			<PageHeader>
				<PageHeader.Title>
					{Language("DELIVERY_CONDITION")}
					<Helmet title={intl.formatMessage({ id: "DELIVERY_CONDITION" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/deliveryconditions")} variant="outlined">
						{Language("CANCEL")}
					</Button>

					<Button loading={loading} type="submit">
						{Language("SAVE")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Grid spacing={3} container>
				<Grid size={{ xs: 12, lg: 6 }}>
					<Card>
						<FormGroup
							label={Language("DELIVERY_CONDITION_KEY")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="key"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} disabled={!!id} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={Language("DELIVERY_CONDITION_DESCRIPTION")}
							labelPlacement="left-center"
							overflow="hidden"
							required>
							<Controller
								name="description"
								control={control}
								render={({ field, formState }) => (
									<React.Fragment>
										<Input {...field} />
										<FormErrorMessage name={field.name} errors={formState.errors} />
									</React.Fragment>
								)} />
						</FormGroup>

						<FormGroup
							label={Language("DELIVERY_CONDITION_SHOW_ON_LOCATION_PLANNING_DESCRIPTION")}
							labelPlacement="left-center">
							<Controller
								name="showOnLocationPlanning"
								control={control}
								render={({ field, formState }) => (
									<FormField errors={formState.errors} name="showOnLocationPlanning">
										<Checkbox {...field} value={undefined} checked={field.value} />
									</FormField>
								)}
							/>
						</FormGroup>

						<FormGroup
							label={Language("DELIVERY_CONDITION_SHOW_ON_BULK_PLANNING_DESCRIPTION")}
							labelPlacement="left-center">
							<Controller
								name="showOnBulkPlanning"
								control={control}
								render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
						</FormGroup>
					</Card>
				</Grid>
			</Grid>
		</form>
	);
};

export default DeliveryConditionCreateEdit;
